import { Box, Typography, IconButton } from '@mui/material';
import { ChevronRight, MoreHoriz } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useState } from 'react';
import { GuardedLink } from '../shared/GuardedLink';

export type BreadcrumbEntityType =
  | 'client' | 'project' | 'site' | 'location'
  | 'sample' | 'workOrder' | 'test';

export interface BreadcrumbItem {
  label: string;
  path?: string;
  entityType?: BreadcrumbEntityType;
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
}

const ENTITY_COLORS: Record<BreadcrumbEntityType, string> = {
  client: '#5C6BC0',
  project: '#4A6741',
  site: '#8B7355',
  location: '#26A69A',
  sample: '#FF8A65',
  workOrder: '#42A5F5',
  test: '#AB47BC',
};

const chipSx = (color: string | undefined, isLast: boolean, clickable: boolean) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1,
  py: 0.25,
  borderRadius: '12px',
  fontSize: '0.8rem',
  fontWeight: isLast ? 600 : 400,
  color: isLast ? (color || 'text.primary') : 'text.secondary',
  bgcolor: isLast && color ? alpha(color, 0.1) : 'transparent',
  textDecoration: 'none',
  maxWidth: 180,
  cursor: clickable ? 'pointer' : 'default',
  transition: 'background-color 0.15s',
  '&:hover': clickable ? { bgcolor: alpha(color || '#000', 0.08) } : {},
});

export const Breadcrumb = ({ items }: BreadcrumbProps) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState(false);

  const shouldCollapse = isSmall && items.length > 4 && !expanded;

  const visibleItems = shouldCollapse
    ? [items[0], null, ...items.slice(-2)] // first, ..., last two
    : items;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.3 }}>
      {visibleItems.map((item, idx) => {
        // Collapsed placeholder
        if (item === null) {
          return (
            <Box key="collapse" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <IconButton size="small" onClick={() => setExpanded(true)}
                sx={{ width: 24, height: 24, color: 'text.disabled' }}>
                <MoreHoriz sx={{ fontSize: 16 }} />
              </IconButton>
              <ChevronRight sx={{ fontSize: 14, color: 'text.disabled' }} />
            </Box>
          );
        }

        const realIdx = shouldCollapse
          ? (idx === 0 ? 0 : items.length - (visibleItems.length - idx))
          : idx;
        const isLast = realIdx === items.length - 1;
        const color = item.entityType ? ENTITY_COLORS[item.entityType] : undefined;
        const clickable = !isLast && !!item.path;

        const chip = (
          <Box sx={chipSx(color, isLast, clickable)} title={item.label}>
            {color && (
              <Box sx={{
                width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                bgcolor: color,
              }} />
            )}
            <Typography component="span" sx={{
              fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {item.label}
            </Typography>
          </Box>
        );

        return (
          <Box key={item.label + realIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
            {clickable ? (
              <GuardedLink to={item.path!} style={{ textDecoration: 'none' }}>
                {chip}
              </GuardedLink>
            ) : chip}
            {!isLast && <ChevronRight sx={{ fontSize: 14, color: 'text.disabled' }} />}
          </Box>
        );
      })}
    </Box>
  );
};
